import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ChevronLeft, ExternalLink, Mail, Trash } from 'lucide-react';
import { useMessages } from '../stores/messages.js';
import { anyOverlayOpen, useUi } from '../stores/ui.js';
import { internalPath } from '../lib/redirect.js';
import { formatDateTimeParis } from '../lib/dates.js';
import { TexteLie } from '../components/TexteLie.js';
import { Button } from '../ui/Button.js';
import { ConfirmDialog } from '../ui/ConfirmDialog.js';
import { EmptyState } from '../components/EmptyState.js';
import type { Message as MessageT } from '../api/messages.js';

const ROUTE_LABELS: Record<string, string> = {
  '/today': 'Today',
  '/board': 'Upcoming',
  '/courses': 'Courses',
  '/tracker': 'Tracker',
  '/calories': 'Calories',
};

/**
 * Le détail d'un message. C'est ce que la notification ouvre : elle porte
 * `/messages/<id>` quand le message n'a pas de destination métier (ADR 0008 § 2).
 */
export function Message(): React.ReactElement {
  const { messageId = '' } = useParams();
  const navigate = useNavigate();
  const fetchOne = useMessages((s) => s.fetchOne);
  const markRead = useMessages((s) => s.markRead);
  const remove = useMessages((s) => s.remove);
  const overlayOpen = useUi(anyOverlayOpen);

  const [message, setMessage] = useState<MessageT | null>(null);
  const [state, setState] = useState<'loading' | 'ready' | 'gone'>('loading');
  const [confirmOpen, setConfirmOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setState('loading');
    fetchOne(messageId)
      .then((m) => {
        if (cancelled) return;
        setMessage(m);
        setState('ready');
        if (!m.read) markRead(m.messageId, true);
      })
      .catch(() => {
        if (!cancelled) setState('gone');
      });
    return () => {
      cancelled = true;
    };
  }, [messageId, fetchOne, markRead]);

  const back = useCallback(() => navigate('/messages'), [navigate]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      // Escape appartient à ce qui est au-dessus : ConfirmDialog (capture) ou n'importe
      // quelle modale globale. Sans ce test, fermer la recherche ramènerait aussi à la liste.
      if (e.key === 'Escape' && !confirmOpen && !overlayOpen) back();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [back, confirmOpen, overlayOpen]);

  async function onDelete(): Promise<void> {
    setConfirmOpen(false);
    await remove(messageId);
    back();
  }

  return (
    <div className="max-w-2xl mx-auto w-full px-4 md:px-6 py-4 md:py-6 space-y-4">
      <Link to="/messages" className="bouton bouton-discret bouton-petit">
        <ChevronLeft size={16} aria-hidden />
        Messages
      </Link>

      {state === 'loading' && (
        <div className="vide">
          <span className="chargement" role="status" aria-label="Chargement" />
        </div>
      )}

      {state === 'gone' && (
        <EmptyState>
          Ce message n&apos;existe plus — les messages sont conservés 90 jours.
        </EmptyState>
      )}

      {state === 'ready' && message && (
        <>
          <div className="space-y-1">
            <h1 className="text-2xl wrap-anywhere"><TexteLie text={message.title} /></h1>
            <p className="discret">{formatDateTimeParis(message.createdAt)}</p>
          </div>

          <CorpsMessage body={message.body} />

          <div className="rangee pt-3 border-t border-edge">
            {message.url && (
              <Button
                variant="secondary"
                size="small"
                // La destination vient de la base : elle repasse par le garde-fou qui protège
                // déjà la redirection post-login (`//host` lu comme une autre origine).
                onClick={() => navigate(internalPath({ pathname: message.url!, search: '' }))}
              >
                <ExternalLink size={16} aria-hidden />
                Ouvrir {ROUTE_LABELS[message.url] ?? message.url}
              </Button>
            )}
            <Button
              variant="ghost"
              size="small"
              onClick={() => {
                markRead(message.messageId, false);
                back();
              }}
            >
              <Mail size={16} aria-hidden />
              Marquer non lu
            </Button>
            <Button variant="danger" size="small" onClick={() => setConfirmOpen(true)}>
              <Trash size={16} aria-hidden />
              Supprimer
            </Button>
          </div>
        </>
      )}

      <ConfirmDialog
        open={confirmOpen}
        title="Supprimer ce message ?"
        message="Le texte sera perdu."
        confirmLabel="Supprimer"
        onConfirm={() => void onDelete()}
        onCancel={() => setConfirmOpen(false)}
      />
    </div>
  );
}

/** Le corps d'un message, sauts de ligne gardés et liens cliquables (le lien de réunion de n8n). */
export function CorpsMessage({ body }: { body: string }): React.ReactElement {
  return (
    <p className="whitespace-pre-wrap wrap-anywhere max-w-prose">
      <TexteLie text={body} />
    </p>
  );
}
